/**
 * VersionPill — floating build indicator (Wave 12.v).
 *
 * Bottom-left chip showing the build commit + environment. Visible to
 * owner / advisor / developer accounts only; clients never see it.
 *
 * Click expands to a calm card with timestamp + a Refresh button so
 * you can confirm a stale bundle has been replaced after deployment.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, RefreshCw, ExternalLink, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function _isPriv(user) {
  if (!user) return false;
  const role = (user.role || "").toLowerCase();
  const roles = new Set((user.roles || []).map((r) => r.toLowerCase()));
  return ["owner", "developer", "advisor"].includes(role) || ["owner", "developer", "advisor"].some((r) => roles.has(r));
}

export default function VersionPill() {
  const { user } = useAuth() || {};
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchInfo = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/owner-access/version`, { params: { _t: Date.now() } });
      setInfo(r.data);
    } catch {
      try {
        const r = await axios.get(`/version.json?_t=${Date.now()}`);
        setInfo(r.data);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => { if (_isPriv(user)) fetchInfo(); }, [user, fetchInfo]);

  if (!_isPriv(user) || !info) return null;
  const env = info.environment || "unknown";
  const envTone = env === "production"
    ? "bg-[hsl(168,22%,32%)]/15 text-[hsl(168,22%,32%)]"
    : env === "preview"
    ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]"
    : "bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))]";

  return (
    <div className="fixed bottom-3 left-3 z-40 print:hidden" data-testid="version-pill">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className={`group inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-[rgba(0,0,0,0.08)] bg-white/90 backdrop-blur-sm font-mono text-[9.5px] uppercase tracking-[0.16em] shadow-sm hover:border-[hsl(var(--accent-gold))]/40`}
          title={`Built ${info.build_human || info.build_timestamp}`}
          data-testid="version-pill-trigger"
        >
          <Sparkles className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <span className={`px-1.5 py-0.5 rounded-sm ${envTone}`}>{env}</span>
          <span className="text-[hsl(var(--foreground-muted))]">{info.git_commit || "—"}</span>
        </button>
      ) : (
        <div className="w-[280px] p-3 rounded-md border border-[rgba(0,0,0,0.08)] bg-white/95 backdrop-blur-sm shadow-md" data-testid="version-pill-expanded">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Build info</p>
              <h4 className="font-serif text-[15px] mt-0.5">{env === "production" ? "Production" : env === "preview" ? "Preview" : "Local"}</h4>
            </div>
            <button onClick={() => setOpen(false)} className="size-6 rounded inline-flex items-center justify-center hover:bg-[hsl(var(--background-paper))]" data-testid="version-pill-close">
              <X className="size-3" />
            </button>
          </div>
          <div className="mt-2 space-y-1 font-mono text-[10.5px]">
            <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[64px]">Built</span><span className="flex-1">{info.build_human || info.build_timestamp || "—"}</span></div>
            <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[64px]">Commit</span><span>{info.git_commit || "n/a"}</span></div>
            <div className="flex gap-2"><span className="text-[hsl(var(--foreground-muted))] w-[64px]">Branch</span><span>{info.git_branch || "n/a"}</span></div>
          </div>
          <div className="mt-3 flex items-center gap-1.5">
            <button onClick={fetchInfo} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[rgba(0,0,0,0.08)] hover:border-[hsl(var(--accent-gold))]/40 font-mono text-[9.5px] uppercase tracking-[0.16em]" data-testid="version-pill-refresh">
              <RefreshCw className="size-2.5" /> Refresh
            </button>
            <Link to="/owner-access" className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[rgba(0,0,0,0.08)] hover:border-[hsl(var(--accent-gold))]/40 font-mono text-[9.5px] uppercase tracking-[0.16em]" data-testid="version-pill-hub">
              Owner Hub <ExternalLink className="size-2.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
